import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import validator from 'validator';
import { toast } from 'react-toastify';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, useMediaQuery, Paper } from '@mui/material';
import { createOrder } from '../../redux/slices/ordersSlice';
import { clearSeats, removeSeat } from '../../redux/slices/cartSlice.js';
import { openFileInNewTab } from '../../utils/fileUtils';
import seatInfoFormatter from '../../utils/formatters';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import FormHelperText from '@mui/material/FormHelperText';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import LanguageSelector from '../LanguageSelector';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import api from './../../api.js';
import printApi from './../../printApi';
import { TIMEOUT_LOCALHOST, TIMEOUT_NONE } from '../../constants';
import { useOrderPolling } from '../../utils/useOrderPolling.js';
import { normalizeOptionalPhone } from '../../utils/phoneUtils';

dayjs.locale('ro');

const CheckoutStep = {
  FIRST: 1,
  SECOND: 2,
  THIRD: 3,
};

const commonConfig = {
  firstName: {
    value: '',
    error: false,
    errorText: 'Câmp necompletat',
  },
  lastName: {
    value: '',
    error: false,
    errorText: 'Câmp necompletat',
  },
  email: {
    value: '',
    error: false,
    errorText: 'Email invalid',
  },
  phone: {
    value: '',
    error: false,
    errorText: 'Număr telefon invalid',
  },

  agreeToTerms: {
    value: false,
    error: false,
    errorText: 'Câmp nebifat',
  },

  paymentType: {
    value: '',
    error: false,
    errorText: 'Mod plata neselectat',
  },

  receiptNr: {
    value: '',
    error: false,
    errorText: 'Câmp necompletat',
  },
};

const optionalFormConfig = {
  firstName: {
    ...commonConfig.firstName,
    isValid: (value) => {
      return true;
    },
  },

  lastName: {
    ...commonConfig.lastName,
    isValid: (value) => {
      return true;
    },
  },

  email: {
    ...commonConfig.email,
    isValid: (value) => {
      return validator.isEmpty(value, { ignore_whitespace: true }) || validator.isEmail(value);
    },
  },

  phone: {
    ...commonConfig.phone,
    isValid: (value) => {
      return normalizeOptionalPhone(value).isValid;
    },
  },

  paymentType: {
    ...commonConfig.paymentType,
    isValid: (value) => {
      return ['CASH', 'CARD'].includes(value);
    },
  },

  receiptNr: {
    ...commonConfig.receiptNr,
    isValid: (value) => {
      return true;
    },
  },

  language: {
    value: 'ro',
    error: false,
    errorText: '',
    isValid: (value) => {
      return ['en', 'de', 'ro', 'hu'].includes(value);
    },
  },
};

const VOUCHER_ERROR_MESSAGE_KEYS = {
  INVALID_VOUCHER_CODE: 'The voucher code is invalid.',
  VOUCHER_NOT_FOUND: 'The voucher code is invalid.',
  VOUCHER_CODE_AMBIGUOUS: 'The voucher code is invalid.',
  VOUCHER_CODE_INACTIVE: 'The voucher code is inactive.',
  VOUCHER_CAMPAIGN_INACTIVE: 'The voucher campaign is inactive.',
  VOUCHER_USAGE_LIMIT_REACHED: 'The voucher usage limit has been reached.',
  VOUCHER_NOT_YET_VALID: 'The voucher is not valid yet.',
  VOUCHER_EXPIRED: 'The voucher has expired.',
  VOUCHER_CUSTOMER_EMAIL_REQUIRED: 'Enter the customer email to verify this voucher.',
  VOUCHER_EMAIL_MISMATCH: 'The customer email does not match this voucher.',
  VOUCHER_ORGANIZER_MISMATCH: 'The voucher is not eligible for the selected tickets.',
  MIN_ELIGIBLE_TICKET_COUNT_NOT_MET: 'The minimum number of eligible tickets has not been reached.',
  NO_ITEM_RECEIVES_DISCOUNT: 'The voucher is not eligible for the selected tickets.',
};

const getVoucherErrorMessageKey = (errorType) =>
  VOUCHER_ERROR_MESSAGE_KEYS[errorType] || 'The voucher could not be verified.';

const FirstStepContent = ({
  seats,
  formState,
  onInputChange,
  isCashier,
  voucherCode,
  onVoucherCodeChange,
  onVerifyVoucher,
  voucherPreview,
  voucherError,
  isVoucherLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reqUserData = false;
  const seatsByEvent = useMemo(() => {
    return seats.reduce((acc, s) => {
      const key = s.eventTitle || 'Eveniment';
      (acc[key] ||= []).push(s);
      return acc;
    }, {});
  }, [seats]);
  const voucherItemsBySeat = useMemo(
    () => new Map((voucherPreview?.items || []).map((item) => [`${String(item.eventId)}:${String(item.id)}`, item])),
    [voucherPreview]
  );
  return (
    <>
      <Typography component="h2" variant="h6">
        {t('Client details')}
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid item xs={6}>
          <TextField
            name="firstName"
            value={formState.firstName.value}
            onChange={onInputChange}
            error={formState.firstName.error}
            helperText={formState.firstName.error ? formState.firstName.errorText : ''}
            required={reqUserData}
            fullWidth
            variant="standard"
            type="text"
            label={t('First Name')}
          ></TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="lastName"
            value={formState.lastName.value}
            onChange={onInputChange}
            error={formState.lastName.error}
            helperText={formState.lastName.error ? formState.lastName.errorText : ''}
            required={reqUserData}
            fullWidth
            variant="standard"
            type="text"
            label={t('Last Name')}
          ></TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            name="email"
            value={formState.email.value}
            onChange={onInputChange}
            error={formState.email.error}
            helperText={formState.email.error ? formState.email.errorText : ''}
            fullWidth
            variant="standard"
            required={reqUserData}
            type="email"
            label={t('Email')}
          ></TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            value={formState.phone.value}
            onChange={onInputChange}
            error={formState.phone.error}
            helperText={formState.phone.error ? formState.phone.errorText : ''}
            name="phone"
            required={reqUserData}
            fullWidth
            variant="standard"
            type="tel"
            label={t('Phone')}
          ></TextField>
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {t('languageSelectInfo')}
      </Typography>

      <LanguageSelector
        language={formState.language.value}
        setLanguage={(newLang) =>
          onInputChange({
            target: { name: 'language', value: newLang },
          })
        }
      />

      {isCashier && (
        <Box sx={{ mt: 3 }}>
          <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
            {t('Voucher')}
          </Typography>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs>
              <TextField
                fullWidth
                name="voucherCode"
                value={voucherCode}
                onChange={(event) => onVoucherCodeChange(event.target.value)}
                label={t('Voucher code')}
                error={Boolean(voucherError)}
                helperText={voucherError ? t(voucherError) : voucherPreview ? t('Voucher verified successfully.') : ' '}
                inputProps={{ maxLength: 64 }}
                variant="standard"
              />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={onVerifyVoucher}
                disabled={isVoucherLoading || !voucherCode.trim() || !seats.length}
                sx={{ mt: 1 }}
              >
                {t('Verify')}
                {isVoucherLoading && <CircularProgress size={18} sx={{ ml: 1 }} />}
              </Button>
            </Grid>
          </Grid>
          {voucherPreview && (
            <Box sx={{ mt: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('Voucher discount')}</Typography>
                <Typography fontWeight={500}>-{Number(voucherPreview.voucherDiscount).toFixed(2)} Lei</Typography>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 4,
        }}
      >
        <Typography component="h2" variant="h6">
          {t('Seats Selected')}
        </Typography>
        {seats.length > 0 && (
          <Button sx={{ p: 1 }} size="small" variant="text" onClick={() => dispatch(clearSeats())}>
            {t('Delete all')}
          </Button>
        )}
      </Box>
      {seats.length ? (
        <List
          subheader={<li />}
          sx={{
            height: 200,
            maxHeight: 400,
            overflowY: 'auto',
            mt: 2,
            pb: 0,
          }}
        >
          {Object.entries(seatsByEvent).map(([eventTitle, items]) => (
            <li key={eventTitle}>
              <Paper
                component="ul"
                elevation={0}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 0,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                }}
              >
                <ListSubheader
                  component="div"
                  disableSticky={false}
                  sx={(theme) => ({
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <Typography variant="subtitle1" fontWeight={400}>
                    {eventTitle}
                  </Typography>
                </ListSubheader>
                {items.map(
                  (
                    {
                      id,
                      eventId,
                      promotionType,
                      singleType,
                      typeId,
                      typeLabel,
                      isDefaultType,
                      section,
                      row,
                      seat,
                      basePrice,
                      discount,
                      price,
                      isFree,
                      categoryLabel,
                    },
                    index
                  ) => {
                    const voucherItem = voucherItemsBySeat.get(`${String(eventId)}:${String(id)}`);
                    const hasVoucherDiscount = Number(voucherItem?.voucherDiscount) > 0;

                    return (
                      <React.Fragment key={`${id}-${index}`}>
                        <ListItem dense>
                          <ListItemText
                            primary={
                              <>
                                {categoryLabel}
                                {!isDefaultType && <Chip sx={{ ml: 1 }} size="small" label={typeLabel} />}
                              </>
                            }
                            secondary={seatInfoFormatter(section, row, seat, t)}
                          />
                          <ListItemSecondaryAction>
                            <Box display="flex" alignItems="center">
                              {hasVoucherDiscount ? (
                                <Stack alignItems="flex-end">
                                  <Typography
                                    component="span"
                                    sx={{ textDecoration: 'line-through', fontSize: '0.75rem' }}
                                  >
                                    {Number(voucherItem.priceBeforeVoucher).toFixed(2)}
                                    {t('Lei')}
                                  </Typography>
                                  <Typography component="span" sx={{ color: 'error.main' }}>
                                    {Number(voucherItem.price).toFixed(2)}
                                    {t('Lei')}
                                  </Typography>
                                </Stack>
                              ) : isFree || discount ? (
                                <Stack alignItems="flex-end">
                                  <Typography
                                    component="span"
                                    sx={{ textDecoration: 'line-through', fontSize: '0.75rem' }}
                                  >
                                    {(isFree ? price : basePrice).toFixed(2)}
                                    {t('Lei')}
                                  </Typography>
                                  <Typography component="span" sx={{ color: 'error.main' }}>
                                    {(isFree ? 0 : price).toFixed(2)}
                                    {t('Lei')}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography component="span">
                                  {price.toFixed(2)}
                                  {t('Lei')}
                                </Typography>
                              )}
                              <IconButton
                                onClick={() => {
                                  const seatObj = {
                                    id,
                                    eventId,
                                    promotionType,
                                    typeId: singleType ? null : String(typeId),
                                  };
                                  dispatch(removeSeat(seatObj));
                                }}
                                edge="end"
                                aria-label="delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < items.length - 1 && <Divider sx={{ m: 0 }} />}
                      </React.Fragment>
                    );
                  }
                )}
              </Paper>
            </li>
          ))}
        </List>
      ) : (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            minHeight: 200,
            borderRadius: 2,
            mt: 2,
            p: 0,
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary,
            }}
          >
            {t('No seat selected')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.text.disabled),
              textAlign: 'center',
            }}
          >
            {t('Select seats from the seating chart to view them here.')}
          </Typography>
        </Paper>
      )}
    </>
  );
};

const FirstStepFooter = ({ isLoading, totalPrice, seats, onAction, actionText }) => {
  const { t } = useTranslation();
  return (
    <Grid sx={{ mt: 2 }} item container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Typography component="h2" variant="h6">
          {t('Total') + ':'}
          {totalPrice.toFixed(2) + ' Lei'}
        </Typography>
      </Grid>
      <Grid item>
        <Button disabled={isLoading || !seats.length} onClick={onAction} variant="contained">
          {actionText}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </Grid>
    </Grid>
  );
};

const SecondStepContent = ({ formState, onInputChange }) => {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" width="100%">
      <DialogContentText gutterBottom sx={{ textAlign: 'center', fontSize: 'large' }}>
        {t("After the payment has been made, click 'Submit order'.")}
      </DialogContentText>
      <DialogContentText gutterBottom sx={{ fontSize: 'large', fontWeight: 'bold' }}>
        {t('Payment type') + ':'}
        {formState.paymentType.value}
      </DialogContentText>
      {formState.paymentType.value === 'CARD' && (
        <TextField
          name="receiptNr"
          value={formState.receiptNr.value}
          onChange={onInputChange}
          error={formState.receiptNr.error}
          helperText={formState.receiptNr.error ? formState.receiptNr.errorText : ''}
          variant="standard"
          type="text"
          label={t('Receipt No.')}
        />
      )}
    </Box>
  );
};

const SecondStepFooter = ({ totalPrice, seats, isLoading, onBack, onAction }) => {
  const { t } = useTranslation();

  return (
    <Grid item container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Typography component="h2" variant="h6">
          {t('Total') + ':'}
          {totalPrice.toFixed(2) + ' Lei'}
        </Typography>
      </Grid>
      <Grid item>
        <Button sx={{ marginRight: 1 }} disabled={isLoading} onClick={onBack} variant="contained">
          {t('Back')}
        </Button>
        <Button disabled={isLoading || !seats.length} onClick={onAction} variant="contained">
          {t('Send order')}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </Grid>
    </Grid>
  );
};

const ThirdStepContent = ({ orderReference, formLanguage }) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState('OFFLINE');
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isLoadingPrinterStatus, setIsLoadingPrinterStatus] = useState(false);
  const [isPrinterConnected, setIsPrinterConnected] = useState(false);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [language, setLanguage] = useState(formLanguage);

  const { isPolling, retryLimitReached } = useOrderPolling(orderReference);

  useEffect(() => {
    if (!orderReference || selectedType !== 'PRINT') return;
    setIsLoadingPrinterStatus(true);

    printApi
      .get('/health', { timeout: TIMEOUT_LOCALHOST })
      .then(async (res) => {
        setIsServerReachable(true);
        res.data.ok ? setIsPrinterConnected(true) : setIsPrinterConnected(false);
        setIsLoadingPrinterStatus(false);
      })
      .catch((err) => {
        setIsLoadingPrinterStatus(false);
        setIsServerReachable(false);
      });
  }, [orderReference, t, selectedType]);

  const downloadTickets = () => {
    api
      .get(`/orders/${orderReference}/tickets?type=${selectedType}${`&language=${language}`}`, {
        timeout: TIMEOUT_NONE,
        responseType: 'blob',
      })
      .then((res) => {
        const { data } = res;
        const tabWindow = window.open('', '_blank');
        openFileInNewTab(tabWindow, data, 'application/pdf');
      })
      .catch((err) => {
        console.log('Error fetching data ', err);
        toast.error(t('An error occured please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  const printTickets = () => {
    setIsLoadingPrint(true);

    api
      .get(`/orders/${orderReference}/ticketsZip?type=${selectedType}${`&language=${language}`}`, {
        timeout: TIMEOUT_NONE,
        responseType: 'blob',
      })
      .then(async (res) => {
        const zipBlob = res.data;

        const formData = new FormData();
        formData.append('zip_file', zipBlob, 'tickets.zip');

        const printRes = await printApi.post('/print', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!printRes.status || printRes.status !== 200) {
          throw new Error('Failed to print');
        }
        setIsLoadingPrint(false);
      })
      .catch(() => {
        setIsLoadingPrint(false);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" width="100%">
      {orderReference && (
        <>
          <Box display="flex" alignItems="center" gap={2} sx={{ marginTop: 4 }}>
            <TextField
              sx={{ width: 221 }}
              disabled={isPolling || retryLimitReached}
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value)}
              select
              label={t('Ticket format')}
            >
              <MenuItem value={'OFFLINE'}>{t('Offline')}</MenuItem>
              <MenuItem value={'ONLINE'}>{t('OnlineTicket')}</MenuItem>
              <MenuItem value={'PRINT'}>{t('ThermalPrinter')}</MenuItem>
            </TextField>
            <LanguageSelector language={language} setLanguage={setLanguage} />
          </Box>
          {(!isPrinterConnected || !isServerReachable) && selectedType === 'PRINT' && !isLoadingPrinterStatus && (
            <FormHelperText error sx={{ mt: 1 }}>
              {!isServerReachable ? t('ServerNotConnected') : !isPrinterConnected ? t('PrinterNotConnected') : ''}
            </FormHelperText>
          )}
          {isLoadingPrinterStatus && <FormHelperText sx={{ mt: 1 }}>{t('PrinterIsConnecting')}</FormHelperText>}
          {selectedType === 'PRINT' ? (
            <Button
              sx={{ marginTop: 4 }}
              disabled={isPolling || retryLimitReached || isLoadingPrint || !isPrinterConnected || !isServerReachable}
              onClick={printTickets}
              width={221}
              size="large"
              variant="contained"
            >
              {(isPolling || isLoadingPrint) && <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />}
              {isPolling ? t('Processing') : t('Print')}
            </Button>
          ) : (
            <Button
              sx={{ marginTop: 4 }}
              disabled={isPolling || retryLimitReached}
              size="large"
              width={221}
              variant="contained"
              onClick={downloadTickets}
            >
              {isPolling && <CircularProgress size={20} sx={{ mr: 1, color: '#868686' }} />}
              {isPolling ? t('Processing') : t('Download tickets')}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

const ThirdStepFooter = () => {
  return <></>;
};

function CheckoutDialog({ open, onClose, seats, setOrderError }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.auth.user);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [step, setStep] = useState(CheckoutStep.FIRST);
  const isLoading = useSelector((state) => state.orders.isLoading);
  const holdToken = useSelector(({ cart: { holdToken } }) => holdToken);
  const [orderReference, setOrderReference] = useState(null);
  const objectIds = useMemo(
    () =>
      seats.map(({ eventId, id, typeId, typeLabel, isDefaultType, price }) => ({
        eventId,
        id,
        typeId,
        typeLabel,
        isDefaultType,
        expectedPrice: price,
      })),
    [seats]
  );
  const totalPrice = useSelector(({ cart: { totalPrice } }) => totalPrice);
  const config = optionalFormConfig;
  const [formState, setFormState] = useState(config);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherPreview, setVoucherPreview] = useState(null);
  const [voucherError, setVoucherError] = useState('');
  const [isVoucherLoading, setIsVoucherLoading] = useState(false);
  const voucherRequestId = useRef(0);
  const isCashier = user?.role === 'cashier';
  const checkoutTotal = voucherPreview ? Number(voucherPreview.total) : totalPrice;

  useEffect(() => {
    voucherRequestId.current += 1;
    setVoucherPreview(null);
    setVoucherError('');
    setIsVoucherLoading(false);
  }, [formState.email.value, objectIds, voucherCode]);

  const verifyVoucher = async () => {
    const normalizedCode = voucherCode.trim();
    if (!normalizedCode) return;

    if (!formState.email.isValid(formState.email.value)) {
      setFormState((currentState) => ({
        ...currentState,
        email: { ...currentState.email, error: true },
      }));
      setVoucherError('Enter a valid customer email to verify the voucher.');
      return;
    }

    const requestId = ++voucherRequestId.current;
    setIsVoucherLoading(true);
    setVoucherError('');
    setVoucherPreview(null);

    try {
      const { data } = await api.post('/orders/voucher-preview', {
        voucher_code: normalizedCode,
        email: formState.email.value.trim() || null,
        object_ids: objectIds,
      });
      if (voucherRequestId.current !== requestId) return;
      setVoucherPreview(data);
    } catch (err) {
      if (voucherRequestId.current !== requestId) return;
      setVoucherError(getVoucherErrorMessageKey(err.response?.data?.errorType));
    } finally {
      if (voucherRequestId.current === requestId) setIsVoucherLoading(false);
    }
  };

  const dispatchCreateOrder = (payload) => {
    dispatch(createOrder(payload))
      .unwrap()
      .then((res) => {
        if (res.data && res.data.orderReference) {
          setOrderReference(res.data.orderReference);
          dispatch(clearSeats());
          setStep(CheckoutStep.THIRD);
        }
      })
      .catch((err) => {
        if (
          err.data &&
          err.data.errorType &&
          err.data.errorType &&
          (err.data.errorType === 'EVENT_STARTED_ERROR' ||
            err.data.errorType === 'EVENT_NOT_AVAILABLE_ERROR' ||
            err.data.errorType === 'PRICE_MISMATCH_ERROR')
        ) {
          setOrderError(err.data.errorType);
          onCloseInternal();
        } else if (
          err.data &&
          err.data.errorType &&
          err.data.errorType &&
          err.data.errorType === 'INVALID_EMAIL_DOMAIN_ERROR'
        ) {
          const newState = Object.assign({}, formState);
          newState['email'].error = true;
          setFormState(newState);
          setStep(CheckoutStep.FIRST);
        } else if (err.data?.errorType && VOUCHER_ERROR_MESSAGE_KEYS[err.data.errorType]) {
          const errorMessage = getVoucherErrorMessageKey(err.data.errorType);
          setVoucherPreview(null);
          setVoucherError(errorMessage);
          setStep(CheckoutStep.FIRST);
          toast.error(t(errorMessage), { position: toast.POSITION.BOTTOM_RIGHT });
        } else {
          toast.error(t('An error occured please try again!'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      });
  };

  const onInputChange = (input) => {
    setFormState((currentState) => ({
      ...currentState,
      [input.target.name]: {
        ...currentState[input.target.name],
        value: input.target.type === 'checkbox' ? input.target.checked : input.target.value,
        error: false,
      },
    }));
  };

  const onBackCashier = () => {
    setStep(CheckoutStep.FIRST);
  };

  const onNextCashier = () => {
    const BLACKLIST = ['receiptNr'];

    const inputs = Object.keys(formState).filter((i) => i && !BLACKLIST.includes(i));
    const newState = Object.assign({}, formState);

    let hasError = false;
    for (const input of inputs) {
      if (input && !newState[input].isValid(newState[input].value)) {
        hasError = true;
        newState[input].error = true;
      }
    }

    if (voucherCode.trim() && !voucherPreview) {
      hasError = true;
      setVoucherError('Verify the voucher code before continuing.');
    }

    setFormState(newState);

    if (!hasError) {
      setStep(CheckoutStep.SECOND);
    }
  };

  const onFinishCashier = () => {
    const newState = Object.assign({}, formState);

    let hasError = false;
    if (newState['paymentType'].value === 'CARD' && !newState['receiptNr'].isValid(newState['receiptNr'].value)) {
      hasError = true;
      newState['receiptNr'].error = true;
    }

    setFormState(newState);

    if (!hasError) {
      const payload = {
        payment_type: newState.paymentType.value,
        receipt_nr: newState.receiptNr.value,
        user: {
          email: newState.email.value,
          first_name: newState.firstName.value,
          last_name: newState.lastName.value,
          phone: normalizeOptionalPhone(newState.phone.value).value,
        },
        object_ids: objectIds,
        hold_token: holdToken,
        language: newState.language.value,
        voucher_code: voucherPreview ? voucherCode.trim() : null,
      };
      dispatchCreateOrder(payload);
    }
  };

  const onFinish = () => {
    const BLACKLIST = ['paymentType', 'receiptNr'];
    const inputs = Object.keys(formState).filter((i) => i && !BLACKLIST.includes(i));
    const newState = Object.assign({}, formState);
    let hasError = false;
    for (const input of inputs) {
      if (!newState[input].isValid(newState[input].value)) {
        hasError = true;
        newState[input].error = true;
      }
    }

    setFormState(newState);

    if (!hasError) {
      const payload = {
        user: {
          email: newState.email.value,
          first_name: newState.firstName.value,
          last_name: newState.lastName.value,
          phone: normalizeOptionalPhone(newState.phone.value).value,
        },
        object_ids: objectIds,
        hold_token: holdToken,
        language: newState.language.value,
      };
      dispatchCreateOrder(payload);
    }
  };

  const onCloseInternal = () => {
    setFormState(optionalFormConfig);
    setVoucherCode('');
    setVoucherPreview(null);
    setVoucherError('');
    setIsVoucherLoading(false);
    setOrderReference(null);
    setStep(CheckoutStep.FIRST);
    onClose();
  };

  const userRole = useMemo(() => {
    if (user && user.role === 'organizer') {
      return ' (' + t('Organizer') + ')';
    } else if (user && user.role === 'cashier') {
      return ' (' + t('Cashier') + ')';
    } else return '';
  }, [user, t]);

  const actionText = useMemo(() => {
    if (user.role === 'cashier') return t('Continue');
    else if (user.role === 'organizer') return t('Send order');
  }, [user, t]);

  const onAction = () => {
    return user && user.role === 'cashier' ? onNextCashier() : onFinish();
  };

  return (
    <Dialog fullWidth maxWidth="sm" fullScreen={fullScreen} open={open}>
      <DialogTitle gutterBottom>
        {t('Complete Order')}
        {userRole}
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {step === CheckoutStep.FIRST ? (
          <FirstStepContent
            {...{
              seats,
              formState,
              onInputChange,
              isCashier,
              voucherCode,
              onVoucherCodeChange: setVoucherCode,
              onVerifyVoucher: verifyVoucher,
              voucherPreview,
              voucherError,
              isVoucherLoading,
            }}
          />
        ) : step === CheckoutStep.SECOND ? (
          <SecondStepContent {...{ formState, onInputChange }} />
        ) : (
          <ThirdStepContent orderReference={orderReference} formLanguage={formState.language.value} />
        )}
      </DialogContent>
      <DialogActions sx={{ paddingTop: 0, paddingLeft: 2.5, paddingRight: 2.5, paddingBottom: 2.5 }}>
        {step === CheckoutStep.FIRST ? (
          <Grid container direction="column">
            {user && user.role === 'cashier' && (
              <Grid item>
                <TextField
                  name="paymentType"
                  required
                  value={formState.paymentType.value}
                  onChange={onInputChange}
                  fullWidth
                  select
                  variant="standard"
                  label={t('Payment type')}
                  error={formState.paymentType.error}
                  helperText={formState.paymentType.error ? formState.paymentType.errorText : ''}
                >
                  <MenuItem value={'CASH'}>{t('CASH')}</MenuItem>
                  <MenuItem value={'CARD'}>{t('CARD')}</MenuItem>
                </TextField>
              </Grid>
            )}
            <FirstStepFooter
              {...{
                user,
                isLoading: isLoading || isVoucherLoading,
                totalPrice: checkoutTotal,
                seats,
                formState,
                onInputChange,
                onAction,
                actionText,
              }}
            />
          </Grid>
        ) : step === CheckoutStep.SECOND ? (
          <SecondStepFooter
            {...{
              isLoading,
              totalPrice: checkoutTotal,
              seats,
              onBack: onBackCashier,
              onAction: onFinishCashier,
            }}
          />
        ) : (
          <ThirdStepFooter onAction={onCloseInternal} />
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CheckoutDialog;
